import { useCallback, useMemo, useRef, useState } from 'react';

import { type Environment } from 'react-relay';

import { AnyAri } from '@atlassian/ari/any-ari';
import { ConfluenceSiteAri } from '@atlassian/ari/confluence/site';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { useUnitLinkedResourceIds } from '@atlassian/unit-linked-resources';

import type { Product } from './global-object-preview-panels';

const anchorBuilder: Partial<Record<Product, (siteId: string) => string>> = {
	jira: (siteId) => JiraSiteAri.create({ siteId }).toString(),
	confluence: (siteId) => ConfluenceSiteAri.create({ siteId }).toString(),
};

const buildAnchorAri = (product: Product, cloudId: string): string => {
	const build = anchorBuilder[product];
	return build && cloudId ? build(cloudId) : '';
};

const extractLinkedCloudIds = (linkedResourceIds: string[]): Set<string> => {
	const cloudIds = new Set<string>();
	for (const ari of linkedResourceIds) {
		try {
			const parsed = AnyAri.parse(ari);
			if (parsed.resourceType === 'site' && parsed.resourceId) {
				cloudIds.add(parsed.resourceId);
			}
		} catch {
			// Ignore unparseable ARIs.
		}
	}
	return cloudIds;
};

interface UseUnitLinkedResourcesResult {
	/**
	 * Returns `true` if `targetCloudId` belongs to the same unit as the current
	 * cloudId. Fails open (returns `true`) when unit isolation is not in effect for
	 * this organisation, when the target matches the current cloudId, when the target
	 * has no cloudId, on the first cross-unit check that triggers the deferred fetch,
	 * and while that fetch is loading or errored.
	 */
	isCloudIdInSameUnit: (targetCloudId: string | undefined) => boolean;
}

interface UseUnitLinkedResourcesOptions {
	/**
	 * Whether unit isolation is actually in effect for the current organisation, i.e.
	 * it has launched units with boundary enforcement on (see `useUnitIsolationEnabled`).
	 * While this is `false` no query is issued and every target is reported as being in
	 * the same unit, so nothing is blocked.
	 */
	enabled?: boolean;
}

/**
 * Resolves whether a target cloudId belongs to the current site's unit, via the
 * `admin_unitLinkedResource` GraphQL query.
 *
 * The query is fetched **lazily**: it does not run on mount. It is only triggered
 * the first time {@link UseUnitLinkedResourcesResult.isCloudIdInSameUnit} is called
 * with a genuine cross-unit target (a truthy cloudId that differs from the current
 * one, when an anchor ARI exists). Pages that never evaluate a cross-unit preview
 * panel therefore never issue the query.
 *
 * This hook is only reached behind the units isolation rollout gates: the top-level
 * `useGlobalObjectPreviewPanels` selects `useGlobalObjectPreviewPanelsNew` (its only
 * caller) via `functionWithCondition`, so no additional gating is needed here. The
 * remaining part of that decision - whether the organisation has actually launched
 * units with boundary enforcement on, which is read from AGG - is passed in as
 * {@link UseUnitLinkedResourcesOptions.enabled}.
 */
export const useUnitLinkedResources = (
	relayEnvironment: Environment,
	product: Product,
	cloudId: string,
	{ enabled = true }: UseUnitLinkedResourcesOptions = {},
): UseUnitLinkedResourcesResult => {
	const anchorAri = useMemo(() => buildAnchorAri(product, cloudId), [product, cloudId]);

	const [hasRequestedFetch, setHasRequestedFetch] = useState(false);

	const { linkedResourceIds, loading, error } = useUnitLinkedResourceIds(
		relayEnvironment,
		anchorAri,
		{ enabled: enabled && hasRequestedFetch },
	);

	const linkedCloudIds = useMemo(
		() => extractLinkedCloudIds(linkedResourceIds),
		[linkedResourceIds],
	);

	// Read via a ref so isCloudIdInSameUnit keeps a stable identity across fetches, and
	// across the organisation's units state resolving.
	const stateRef = useRef({ linkedCloudIds, loading, error, hasRequestedFetch, enabled });
	stateRef.current = { linkedCloudIds, loading, error, hasRequestedFetch, enabled };

	const fetchScheduledRef = useRef(false);

	const isCloudIdInSameUnit = useCallback(
		(targetCloudId: string | undefined): boolean => {
			const { linkedCloudIds, loading, error, hasRequestedFetch, enabled } = stateRef.current;

			// The organisation is not isolating units (or has not been confirmed to yet), so
			// every resource stays visible and there is nothing to query.
			if (!enabled) {
				return true;
			}

			if (!anchorAri || !targetCloudId || targetCloudId === cloudId) {
				return true;
			}

			// This runs during the consumer's render, where React forbids setState.
			// Defer it to a microtask so the fetch is triggered after render finishes.
			if (!hasRequestedFetch && !fetchScheduledRef.current) {
				fetchScheduledRef.current = true;
				queueMicrotask(() => setHasRequestedFetch(true));
			}

			// Fail open until the fetch has been requested and settled with data.
			if (!hasRequestedFetch || loading || error) {
				return true;
			}

			return linkedCloudIds.has(targetCloudId);
		},
		[anchorAri, cloudId],
	);

	return { isCloudIdInSameUnit };
};
